import { formatPrice } from '../../../../utils/format-price'
import { COLOR_PROPERTY } from './color'

import { type Item, type AttributeSelectorMarker } from '../models'
import { type Stocks, stockStatusMap, getStockLevelStatus } from './stock'

type AttrDataAux =
  | {
      propertyName: string
      attrIndex: number
      values: {
        [x: string]: AttrDataAux
      }
    }
  | object

export type AttrData = AttrDataAux

type VariantAttrValues = {
  [x: string]: string
}

export type AttributeSelector = {
  name: string
  label?: string
  showPrice?: boolean
}

type SelectorValues = {
  [x: string]: {
    attrIndex: number
    values: string[]
  }
}

type VariantValues = { [x: string]: string }

export const getVariantAttrData = (
  attrList: AttributeSelector[],
  variant: Item
): VariantValues =>
  attrList.reduce((acc, curr) => {
    const { name } = curr
    const propertyValue = variant?.[name]
    return {
      ...acc,
      ...(propertyValue ? { [name]: propertyValue } : {})
    }
  }, {})

const calcAttrData = (
  propName: string,
  accData: AttrData,
  data: {
    variantAttrValues: VariantAttrValues
    attrList: AttributeSelector[]
    attrIndex: number
  }
): AttrData => {
  if (propName) {
    const { values: accValues } = { ...accData }
    const { variantAttrValues, attrList, attrIndex } = { ...data }
    const value = variantAttrValues[propName]
    const nextAttrName = attrList[attrIndex + 1]?.name
    const nextAcc = accValues?.[value] ?? {}
    return {
      ...accData,
      propertyName: propName,
      attrIndex,
      values: {
        ...accValues,
        [value]: calcAttrData(nextAttrName, nextAcc, {
          variantAttrValues,
          attrList,
          attrIndex: attrIndex + 1
        })
      }
    }
  }

  return {}
}

const calcAttrSelectors = (
  attrData: AttrData,
  variantAttrValues: VariantAttrValues
): SelectorValues => {
  const { propertyName, attrIndex, values } = { ...attrData }
  if (propertyName) {
    const nestedObj = values?.[variantAttrValues[propertyName]]
    if (nestedObj) {
      return {
        [propertyName]: {
          attrIndex: Number(attrIndex),
          values: Object.keys(values)
        },
        ...calcAttrSelectors(nestedObj, variantAttrValues)
      }
    }
  }
  return {}
}

const calcAttrCurrPrice = (variant: Item, locale: string) => {
  if (variant?.price?.priceDiscount?.value) {
    return formatPrice(
      Number(variant?.price.priceDiscount?.value),
      variant?.price?.priceDiscount?.priceCurrency,
      locale
    )
  }
  return formatPrice(
    Number(variant?.price?.value),
    variant?.price?.priceCurrency,
    locale
  )
}

const calcAttrVariants = ({
  name,
  index,
  attrSelectorValues,
  stocks,
  variantList,
  variantValues,
  locale,
  markers,
  showPrice = false
}: {
  name: string
  index: number
  attrSelectorValues: SelectorValues
  stocks: Stocks
  variantList: Item[]
  variantValues: VariantValues
  locale: string
  markers?: AttributeSelectorMarker[]
  showPrice?: boolean
}) =>
  attrSelectorValues[name]?.values?.map((i) => {
    const variantArray =
      variantList.filter(
        (v: Item) =>
          v[name] === i &&
          Object.entries(attrSelectorValues)
            .filter(([, value]) => index > value?.attrIndex)
            .every(([key]) => v[key] === variantValues[key])
      ) ?? []

    const currVariant: Item =
      variantArray?.find((v: Item) =>
        Object.entries(attrSelectorValues)
          .filter(([, value]) => index < value?.attrIndex)
          .every(([key]) => v[key] === variantValues[key])
      ) ?? variantArray[0]
    const value = currVariant?.[name] as string
    const label = markers?.find((m) => m.value === value)?.label ?? value
    return {
      value,
      attributeLabel: showPrice
        ? `${label}<br>${calcAttrCurrPrice(currVariant, locale)}`
        : `${label}`,
      stock: stockStatusMap[getStockLevelStatus(currVariant, stocks)],
      property: name
    }
  }) ?? []

export const getAttributeSelectors = ({
  attributeSelectorsProp,
  stocks,
  variantList,
  variant,
  attributeSelectorMarkers,
  locale
}: {
  attributeSelectorsProp: AttributeSelector[]
  stocks: Stocks
  variantList: Item[]
  variant: Item
  attributeSelectorMarkers: AttributeSelectorMarker[]
  locale: string
}) => {
  const attrs = [
    { name: COLOR_PROPERTY } as AttributeSelector,
    ...attributeSelectorsProp
  ]?.filter((attr) => variant?.[attr.name])

  const attrSchema = variantList?.reduce(
    (acc, curr) => ({
      ...acc,
      ...calcAttrData(attrs[0]?.name, acc, {
        variantAttrValues: getVariantAttrData(attrs, curr),
        attrList: attrs,
        attrIndex: 0
      })
    }),
    {}
  )
  const variantValues = getVariantAttrData(attrs, variant)
  const attrSelectorValues = calcAttrSelectors(attrSchema, variantValues)
  const selectors = attrs
    .map((a, index) => {
      const { name, label, showPrice } = { ...a }
      const currAttributeValue = variant?.[name] as string
      return {
        currentValue: currAttributeValue,
        property: name,
        name: label,
        label,
        attributeVariants: calcAttrVariants({
          name,
          index,
          attrSelectorValues,
          stocks,
          variantList,
          variantValues,
          markers: attributeSelectorMarkers,
          showPrice,
          locale
        })
      }
    })
    ?.filter(
      (attr) =>
        attr.currentValue &&
        attr.property !== COLOR_PROPERTY &&
        attr?.attributeVariants?.length
    )

  const attributeValues =
    selectors
      ?.map((selector) => ({
        property: selector.property,
        value: variant?.[selector?.property] as string
      }))
      ?.filter((selector) => selector.value) ?? null

  return {
    attributeValues,
    attributeSelectors: selectors
      ?.map((item) => ({
        ...item,
        attributeVariants:
          item.attributeVariants?.length > 1 ? item.attributeVariants : []
      }))
      ?.filter((attr) => attr?.attributeVariants?.length)
  }
}
